/**
 * "Geen klant gevonden: koppelen of nieuwe klant" (plak 5.5).
 *
 * Dezelfde banner die `KoppelingBanner.tsx` in de mailinbox toont, maar
 * op de kaart en bij de zaak in plaats van bij één mail. De voorstellen
 * komen deterministisch uit `klant_koppeling`; er wordt hier niets
 * gescoord en niets gesorteerd op waarschijnlijkheid.
 *
 * Twee regels van deze repo die je hier terugziet:
 *
 * - **Uitgeschakeld = uitleggen waarom.** Kan er niet gekoppeld worden,
 *   dan staat de knop uit mét de reden ernaast. Nooit stil verbergen.
 * - **Een knop belooft niets wat hij niet doet.** Dit portaal maakt geen
 *   klant aan: de klantenbak is Lightspeed. "Nieuwe klant" opent
 *   daarom Lightspeed in een nieuw tabblad, en dat staat er ook bij.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { werkplekKoppelingApi, type KoppelVoorstel } from "@/lib/werkplek-api";

import { useKoppelBanner } from "./koppelbannerVraag";
import { IcoonExtern } from "./pictogrammen";
import { datum } from "./tijd";

/** De match-types uit `klant_koppeling`, in gewone taal. */
const MATCH_LABEL: Record<string, string> = {
  afzender_naam: "zelfde naam als afzender",
  ordernummer: "ordernummer in het bericht",
  huisdiernaam: "naam van het huisdier",
  telefoonnummer: "telefoonnummer in het bericht",
  email_in_body: "e-mailadres in het bericht",
  postcode_huisnummer: "postcode en huisnummer",
};

function Voorstel({
  voorstel,
  bezig,
  opKoppel,
  opNegeer,
}: {
  voorstel: KoppelVoorstel;
  bezig: boolean;
  opKoppel: () => void;
  opNegeer: () => void;
}) {
  return (
    <li className="wp-koppel-rij" data-voorstel={voorstel.voorstel_id}>
      <span className="wp-koppel-wie">
        <b>{voorstel.naam ?? "Klant zonder naam"}</b>
        {voorstel.woonplaats ? <span> · {voorstel.woonplaats}</span> : null}
        {voorstel.email ? <span> · {voorstel.email}</span> : null}
      </span>
      <span className="wp-koppel-waarom">
        Gevonden op {MATCH_LABEL[voorstel.match_type] ?? voorstel.match_type}
        {voorstel.laatste_order_op ? (
          <> · laatste bestelling {datum(voorstel.laatste_order_op)}</>
        ) : (
          <> · nog geen bestelling</>
        )}
      </span>
      <span className="wp-koppel-knoppen">
        <button
          type="button"
          className="wp-knop wp-knop-primair"
          disabled={bezig}
          onClick={opKoppel}
        >
          Dit is de klant
        </button>
        <button
          type="button"
          className="wp-knop"
          disabled={bezig}
          onClick={opNegeer}
        >
          Niet deze
        </button>
      </span>
    </li>
  );
}

export function KoppelBanner({ zaakId }: { zaakId: string }) {
  const cache = useQueryClient();
  const vraag = useKoppelBanner(zaakId);

  const koppelen = useMutation({
    mutationFn: (voorstel: KoppelVoorstel) =>
      werkplekKoppelingApi.koppel(
        zaakId,
        voorstel.klant_id,
        voorstel.voorstel_id,
      ),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ["werkplek", "koppeling", zaakId] });
      void cache.invalidateQueries({ queryKey: ["werkplek", "zaak", zaakId] });
      void cache.invalidateQueries({ queryKey: ["werkplek", "kaarten"] });
    },
  });

  const negeren = useMutation({
    mutationFn: (voorstel: KoppelVoorstel) =>
      werkplekKoppelingApi.negeer(zaakId, voorstel.voorstel_id),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ["werkplek", "koppeling", zaakId] });
    },
  });

  // Nog aan het laden, of er is een klant: dan hoort er niets te staan.
  if (vraag.isLoading || !vraag.data || !vraag.data.nodig) return null;

  const banner = vraag.data;
  const bezig = koppelen.isPending || negeren.isPending;
  const mislukt = koppelen.isError || negeren.isError;

  return (
    <div className="wp-blok wp-koppel" data-koppelbanner role="group">
      <h4>Geen klant gevonden</h4>
      <p className="wp-koppel-kop">
        {banner.afzender
          ? /^[0-9 +]|onbekend nummer/.test(banner.afzender)
            ? `Dit gesprek komt van ${banner.afzender} en hoort nog bij geen enkele klant.`
            : `Dit bericht komt van ${banner.afzender} en hoort nog bij geen enkele klant.`
          : "Dit bericht hoort nog bij geen enkele klant."}
      </p>

      {banner.voorstellen.length > 0 ? (
        <ul className="wp-koppel-lijst">
          {banner.voorstellen.map((voorstel) => (
            <Voorstel
              key={voorstel.voorstel_id}
              voorstel={voorstel}
              bezig={bezig || !banner.mag_koppelen}
              opKoppel={() => koppelen.mutate(voorstel)}
              opNegeer={() => negeren.mutate(voorstel)}
            />
          ))}
        </ul>
      ) : (
        <p className="wp-leegregel">
          Er zijn geen klanten gevonden die hierbij passen. Er wordt alleen
          op exacte treffers gezocht, dus een bijna-gelijke naam levert
          bewust niets op.
        </p>
      )}

      {!banner.mag_koppelen && banner.reden_uitgeschakeld ? (
        <p className="wp-leegregel" data-reden-uit>
          {banner.reden_uitgeschakeld}
        </p>
      ) : null}

      {mislukt ? (
        <p className="wp-koppel-fout" role="alert">
          Dat is niet gelukt. Probeer het opnieuw; blijft dit staan, dan is
          de API niet bereikbaar.
        </p>
      ) : null}

      <p className="wp-koppel-nieuw">
        {banner.nieuwe_klant_url ? (
          <a
            href={banner.nieuwe_klant_url}
            target="_blank"
            rel="noreferrer"
            className="wp-knop"
          >
            <IcoonExtern maat={12} />
            Nieuwe klant in Lightspeed
          </a>
        ) : null}
        <span className="wp-leegregel">
          Een klant aanmaken doet dit portaal niet zelf. Lightspeed is de
          klantenbak; na het aanmaken koppel je hem hier.
          {banner.nieuwe_klant_url
            ? null
            : " De link naar Lightspeed staat nog niet in de instellingen."}
        </span>
      </p>
    </div>
  );
}
